<template>
  <div class="custom-range-picker" ref="picker">
    <div class="custom-select-container-horizontal">
      <label class="custom-select-label-horizontal">Time</label>
      <div class="custom-select-wrapper" ref="trigger" @click="toggleVisible">
        <div class="custom-select-input">
          <span class="value">
            {{
              selectedStartDate && selectedEndDate
                ? `${selectedStartDate}-${selectedEndDate}`
                : "please select"
            }}
          </span>
          <img
            class="arrow-img"
            :src="visible ? arrowGrayDownDown : arrowGrayDown"
            alt="arrow"
          />
        </div>
      </div>
    </div>
    <!-- 弹窗 -->
    <div
      v-if="visible"
      ref="panel"
      class="dropdown-panel"
      :style="dropdownStyle"
    >
      <!-- 输入框区域 -->
      <div class="inputs-wrapper">
        <div class="input-group" :class="{ active: activeInput === 'start' }">
          <div class="label">Start</div>
          <input
            type="text"
            placeholder="Start Date"
            v-model="tempStartDate"
            @input="onInputChange('start', $event.target.value)"
            @focus="activeInput = 'start'"
            @blur="activeInput = null"
          />
        </div>

        <div class="input-group" :class="{ active: activeInput === 'end' }">
          <div class="label">Finish</div>
          <input
            type="text"
            placeholder="End Date"
            v-model="tempEndDate"
            @input="onInputChange('end', $event.target.value)"
            @focus="activeInput = 'end'"
            @blur="activeInput = null"
          />
        </div>
      </div>

      <!-- 星期 -->
      <div class="weekdays">
        <span v-for="d in weekdays" :key="d">{{ d }}</span>
      </div>

      <!-- 日历滚动 -->
      <div class="scroll-calendar" ref="scrollContainer">
        <div
          v-for="m in months"
          :key="m.year + '-' + m.month"
          class="month-block"
          :data-year="m.year"
          :data-month="m.month"
        >
          <div class="month-title">
            {{ m.year }}/{{ m.month < 10 ? "0" + m.month : m.month }}
          </div>
          <div class="dates-grid">
            <span
              v-for="(d, index) in m.days"
              :key="d.fullDate || index"
              class="date-cell"
              :class="{
                'start-date': d.fullDate && isStartDate(d.fullDate),
                'end-date': d.fullDate && isEndDate(d.fullDate),
                'show-rect': d.fullDate && showRect(d, index),
                'in-range': d.fullDate && isInRange(d.fullDate),
                'range-left':
                  d.fullDate &&
                  isInRange(d.fullDate) &&
                  (index % 7 === 0 || d.day === 1),
                'range-right':
                  d.fullDate &&
                  isInRange(d.fullDate) &&
                  (index % 7 === 6 || index === m.days.length - 1),
                'empty-cell': !d.fullDate,
              }"
              @click="d.fullDate && selectDate(d.fullDate)"
            >
              {{ d.day }}
            </span>
          </div>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="action-buttons">
        <div @click="cancelSelection">Cancel</div>
        <div @click="applySelection">Apply</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  nextTick,
  watch,
  defineProps,
  defineEmits,
  onMounted,
  onBeforeUnmount,
} from "vue";
import dayjs from "dayjs";
import arrowGrayDown from "@/assets/timer_gray_arraw_down.svg";
import arrowGrayDownDown from "@/assets/timer_gray_arraw_downdo.svg";
const activeInput = ref(null);
const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({ start: null, end: null }),
  },
});
const emits = defineEmits(["update:modelValue", "close"]);

const handleClickOutside = (event) => {
  // 如果 panel 或 trigger 存在，且点击目标不在其中
  if (
    panel.value &&
    !panel.value.contains(event.target) &&
    trigger.value &&
    !trigger.value.contains(event.target)
  ) {
    visible.value = false;
    emits("close");
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
const selectedStartDate = ref(props.modelValue.start);
const selectedEndDate = ref(props.modelValue.end);
const tempStartDate = ref(selectedStartDate.value);
const tempEndDate = ref(selectedEndDate.value);

const visible = ref(false);
const trigger = ref(null);
const panel = ref(null);
const scrollContainer = ref(null);

const dropdownStyle = ref({
  top: "0px",
  left: "0px",
  minWidth: "0px",
  position: "absolute",
  zIndex: 1000,
});

// 切换弹窗
const toggleVisible = () => {
  visible.value = !visible.value;
  if (visible.value) {
    nextTick(() => {
      updateDropdownPosition();
      scrollToMonth(selectedStartDate.value || dayjs().format("YYYY-MM-DD"));
    });
  }
};

// 修正位置计算（始终在控件正下方，如果空间不足翻转）
const updateDropdownPosition = () => {
  if (!trigger.value || !panel.value) return;

  const triggerRect = trigger.value.getBoundingClientRect();
  const panelHeight = panel.value.offsetHeight;

  const spaceBelow = window.innerHeight - triggerRect.bottom;
  const spaceAbove = triggerRect.top;

  let topPos = 0;

  if (spaceBelow >= panelHeight || spaceBelow >= spaceAbove) {
    // 下方空间足够 或 下方空间比上方多
    topPos = triggerRect.bottom + window.scrollY;
  } else {
    // 下方空间不足，显示在上方
    topPos = triggerRect.top + window.scrollY - panelHeight;
  }

  dropdownStyle.value.top = `${topPos}px`;
  dropdownStyle.value.left = `${triggerRect.left + window.scrollX}px`;
  dropdownStyle.value.minWidth = `${triggerRect.width}px`;
};

// 同步外部 modelValue
watch(
  () => props.modelValue,
  (val) => {
    selectedStartDate.value = val.start;
    selectedEndDate.value = val.end;
    tempStartDate.value = val.start;
    tempEndDate.value = val.end;
  }
);

// 月份数据
const months = computed(() => {
  const arr = [];
  const start = dayjs("2023-01-01").startOf("month");
  const end = dayjs().endOf("month");
  let current = start.clone();

  while (current.isBefore(end) || current.isSame(end, "month")) {
    const year = current.year();
    const month = current.month() + 1;
    const days = [];
    const firstDayOfWeek = current.startOf("month").day();
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push({ day: "", fullDate: null });
    }
    const lastDay = current.endOf("month").date();
    for (let i = 1; i <= lastDay; i++) {
      const d = current.date(i);
      days.push({ day: i, fullDate: d.format("YYYY/MM/DD") });
    }
    arr.push({ year, month, days });
    current = current.add(1, "month");
  }
  return arr;
});

const weekdays = ["SUN", "MON", "TUES", "WED", "THU", "FRI", "SAT"];

const scrollToMonth = (date) => {
  nextTick(() => {
    if (!scrollContainer.value) return;
    const container = scrollContainer.value;
    const targetDate = dayjs(date);
    const targetBlock = container.querySelector(
      `.month-block[data-year="${targetDate.year()}"][data-month="${
        targetDate.month() + 1
      }"]`
    );
    if (targetBlock) {
      const prevBehavior = container.style.scrollBehavior;
      container.style.scrollBehavior = "auto";
      container.scrollTop = targetBlock.offsetTop;
      container.style.scrollBehavior = prevBehavior;
    }
  });
};

// 选择日期逻辑
const selectDate = (date) => {
  if (!tempStartDate.value || (tempStartDate.value && tempEndDate.value)) {
    tempStartDate.value = date;
    tempEndDate.value = null;
  } else {
    if (dayjs(date).isBefore(dayjs(tempStartDate.value))) {
      tempEndDate.value = tempStartDate.value;
      tempStartDate.value = date;
    } else {
      tempEndDate.value = date;
    }
  }
};

const isStartDate = (date) => date === tempStartDate.value;
const isEndDate = (date) => date === tempEndDate.value;
const isInRange = (date) => {
  if (!tempStartDate.value || !tempEndDate.value) return false;
  return (
    dayjs(date).isAfter(dayjs(tempStartDate.value)) &&
    dayjs(date).isBefore(dayjs(tempEndDate.value))
  );
};
const hasRange = () => {
  if (!tempStartDate.value || !tempEndDate.value) return false;
  return dayjs(tempEndDate.value).diff(dayjs(tempStartDate.value), "day") > 1;
};
const showRect = (d, index) => {
  if (!tempStartDate.value || !tempEndDate.value) return false;
  if (isStartDate(d.fullDate)) {
    return (
      index % 7 !== 6 &&
      !dayjs(d.fullDate).isSame(dayjs(d.fullDate).endOf("month"), "day") &&
      hasRange()
    );
  }
  if (isEndDate(d.fullDate)) {
    return index % 7 !== 0 && d.day !== 1 && hasRange();
  }
  return false;
};

const onInputChange = (type, value) => {
  if (!dayjs(value, "YYYY-MM-DD", true).isValid()) return;
  type === "start"
    ? (tempStartDate.value = value)
    : (tempEndDate.value = value);
};

const cancelSelection = () => {
  tempStartDate.value = selectedStartDate.value;
  tempEndDate.value = selectedEndDate.value;
  emits("update:modelValue", {
    start: selectedStartDate.value,
    end: selectedEndDate.value,
  });
  visible.value = false;
  emits("close");
};

const applySelection = () => {
  selectedStartDate.value = tempStartDate.value;
  selectedEndDate.value = tempEndDate.value;
  emits("update:modelValue", {
    start: selectedStartDate.value,
    end: selectedEndDate.value,
  });
  visible.value = false;
  emits("close");
};
</script>

<style scoped>
.custom-range-picker {
  font-family: "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, "Helvetica Neue", Arial, sans-serif;
}
.date-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 33px;
  border-radius: 8px;
  border: 1px solid #8e67ea;
  padding: 0 8px;
  cursor: pointer;
  background: #fff;
}
.value {
  font-size: 12px;
  color: #9fa1a8;
}
.arrow-down {
  width: 12px;
  height: 12px;
}
.dropdown-panel {
  position: fixed;
  width: 300px;
  max-height: 400px;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}
.inputs-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.inputs-wrapper input {
  width: 100px;
  height: 28px;
  padding: 0 6px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 12px;
}
.inputs-wrapper span {
  font-size: 12px;
}
.scroll-calendar {
  max-height: 280px;
  overflow-y: auto;
  scroll-behavior: smooth;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
  padding: 4px 0;
}
.month-block {
  margin-bottom: 16px;
}
.month-title {
  font-family: "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, "Helvetica Neue", Arial, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0%;
  margin-bottom: 5px;
  color: #333333;
}
.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  margin-bottom: 8px;
  color: #333333;
  font-family: "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, "Helvetica Neue", Arial, sans-serif;
  font-weight: 500;
  font-style: normal;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0%;
}
.dates-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  font-size: 12px;
  gap: 0; /* 去掉间隔 */
}

.date-cell {
  width: 100%;
  height: 28px;
  margin: 0;
  position: relative;
  z-index: 1;
  /* 垂直水平居中 */
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000;
  font-size: 12px;
  margin-top: 4px;
}

/* 区间背景填充（中间的日期） */
.date-cell.in-range {
  background-color: #ebe4fb;
  border-radius: 0; /* 中间矩形无圆角 */
  color: inherit; /* 文字颜色不变 */
}

.date-cell.start-date,
.date-cell.end-date {
  position: relative;
  z-index: 1;
  color: #fff; /* 文字白色 */
}
/* 第二层圆形背景，覆盖在起始和结束 */
/* 整个圆形背景 */
.date-cell.start-date.show-rect::before {
  content: "";
  position: absolute;
  bottom: 0;
  left: 20%;
  width: 100%;
  height: 28px; /* 底部矩形高度 */
  background-color: #ebe4fb;
  border-top-left-radius: 28px;
  border-bottom-left-radius: 28px;
  z-index: -2;
}
.date-cell.end-date.show-rect::before {
  content: "";
  position: absolute;
  bottom: 0;
  right: 20%;
  width: 100%;
  height: 28px; /* 底部矩形高度 */
  background-color: #ebe4fb;
  border-top-right-radius: 28px;
  border-bottom-right-radius: 28px;
  z-index: -2;
}

/* 上方圆形背景保持不变 */
.date-cell.start-date::after,
.date-cell.end-date::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #8e67ea;
  transform: translate(-50%, -50%);
  z-index: -1;
}
.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 24px;
  margin-top: 12px;
}
.action-buttons div {
  font-family: "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, "Helvetica Neue", Arial, sans-serif;
  font-weight: 500;
  font-style: normal;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0%;
  margin-top: 5px;
  color: #8e67ea;
  cursor: pointer; /* 鼠标变手型 */
  transition: all 0.2s ease; /* 平滑过渡 */
}

.date-cell.in-range.range-left {
  border-top-left-radius: 18px;
  border-bottom-left-radius: 18px;
}

.date-cell.in-range.range-right {
  border-top-right-radius: 18px;
  border-bottom-right-radius: 18px;
}
.input-group {
  display: flex;
  flex-direction: column; /* label 在上，input 在下 */
  align-items: flex-start;
}

.input-group .label {
  font-family: "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, "Helvetica Neue", Arial, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0%;
  color: #9fa1a8;
  margin-bottom: 4px;
}

.input-group input {
  width: 100px;
  height: 28px;
  padding: 0 6px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 12px;
  outline: none;
}

/* 激活时样式 */
.input-group.active .label {
  color: #8e67ea;
  font-family: "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, "Helvetica Neue", Arial, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0%;
}

.input-group.active input {
  border-color: #8e67ea;
}

.custom-select-container-horizontal {
  display: flex;
  align-items: center;
  gap: 12px;
}
.custom-select-label-horizontal {
  font-size: 12px;
  color: #333333;
  text-align: right;
  font-family: "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, "Helvetica Neue", Arial, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0%;
}
.custom-select-wrapper {
  position: relative;
  min-width: 200px;
  cursor: pointer;
  font-size: 12px;
  color: #9fa1a8;
}
.custom-select-input {
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  padding: 6px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: border 0.3s;
}
.custom-select-wrapper:hover .custom-select-input {
  border-color: #8e67ea;
}
</style>
